/**
 * =============================================================================
 * Hook: useReferralCampaigns
 * Versão: 2.0.0 (Refator atomicidade — 2026-04-26)
 *
 * Descrição:
 *   CRUD das campanhas de indicação (`affiliate_campaigns`) do tenant ativo.
 *   - Lista (com nome do programa via JOIN), suporta filtro por programa.
 *   - Mutations (create/update/delete) agora chamam RPCs SECURITY DEFINER:
 *       - upsert_referral_campaign  (atomicidade + validações server-side)
 *       - delete_referral_campaign  (Safe Delete server-side)
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useReferralCampaigns');

export type AffiliateCampaign =
  Database['public']['Tables']['affiliate_campaigns']['Row'];

export interface AffiliateCampaignWithProgram extends AffiliateCampaign {
  program_name: string | null;
}

/**
 * v3.0.0 — Campanha enriquecida com métricas agregadas (todos os afiliados).
 * Vem da RPC `list_tenant_campaigns_with_metrics`, que delega o cálculo
 * ao helper único `_calc_campaign_metrics` (mesmo helper consumido pelo
 * portal do afiliado em `list_my_campaigns_with_metrics`).
 */
export interface AffiliateCampaignWithMetrics extends AffiliateCampaignWithProgram {
  clicks_count: number;
  referrals_count: number;
  conversions_count: number;
  earned_amount: number;
  pending_amount: number;
  /** v1.4.0 — Receita total atribuída (SUM conversion_value de referrals convertidos). */
  total_sales: number;
  currency: string;
}

/** Payload "amigável" usado pelos modais. */
export interface ReferralCampaignPayload {
  program_id: string;
  name: string;
  slug: string;
  description: string | null;
  reward_type:
    | 'percentage'
    | 'fixed'
    | 'points'
    | 'tiered'
    | 'recurring'
    | null;
  reward_value: number | null;
  starts_at: string | null;
  ends_at: string | null;
  max_per_affiliate: number | null;
  max_referrals: number | null;
  destination_url: string | null;
  default_utm_source: string | null;
  default_utm_medium: string | null;
  default_utm_campaign: string | null;
  default_utm_content: string | null;
  tiered_rules: any | null;
  is_active: boolean;
  // Landing
  landing_mode: 'redirect' | 'landing' | 'builder';
  landing_title: string | null;
  landing_subtitle: string | null;
  landing_hero_image_url: string | null;
  landing_cta_label: string;
  landing_thank_you_title: string;
  landing_thank_you_message: string;
  landing_redirect_after_submit_seconds: number | null;
  landing_form_fields: any;
  // CRM
  crm_inherit_from_program: boolean;
  auto_create_deal: boolean;
  crm_workspace_id: string | null;
  crm_pipeline_id: string | null;
  crm_phase_id: string | null;
  crm_source: string | null;
  crm_default_seller_id: string | null;
  crm_distribution_queue_id: string | null;
  crm_default_tags: string[];
  crm_deal_title_template: string | null;
  /**
   * v1.3.0 — Modo de atribuição (mutex vendedor x fila).
   * NULL na campanha = herda integralmente do programa em runtime.
   */
  crm_assignment_mode?: 'fixed_seller' | 'queue' | null;
  // v2.0.0 (2026-05-01) — Capa visual + Regras (modelo de contrato)
  cover_media_url?: string | null;
  cover_media_type?: 'image' | 'video' | null;
  cover_storage_path?: string | null;
  terms_template_id?: string | null;
  // v2.1.0 (2026-08-17) — campos exibidos no formulário de nova indicação
  // do painel do afiliado (Nome e WhatsApp são sempre exibidos).
  referral_form_show_email?: boolean;
  referral_form_show_message?: boolean;
}

const QK = (tenantId?: string | null, programId?: string | null) => [
  'referral-campaigns',
  tenantId,
  programId ?? 'all',
];

/**
 * Lista campanhas do tenant ativo com métricas agregadas (cliques, indicações,
 * conversões e ganho), opcionalmente filtradas por programa.
 *
 * v3.0.0 (2026-05-01): migrado para RPC `list_tenant_campaigns_with_metrics`
 * (SECURITY DEFINER) — elimina `SELECT *` do frontend e centraliza a lógica
 * de agregação no helper SQL `_calc_campaign_metrics`.
 */
export function useReferralCampaigns(programId?: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery<AffiliateCampaignWithMetrics[]>({
    queryKey: QK(tenantId, programId),
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)(
        'list_tenant_campaigns_with_metrics',
        {
          p_program_id: programId ?? null,
          p_tenant_hint: tenantId,
        },
      );
      if (error) {
        log.error('Erro ao listar campanhas', error);
        throw error;
      }
      // RPC já entrega a forma final (incl. program_name e métricas).
      // Normalizamos números para garantir que numéricos do PG cheguem como Number.
      return ((data as any[]) ?? []).map((row) => ({
        ...row,
        clicks_count: Number(row.clicks_count ?? 0),
        referrals_count: Number(row.referrals_count ?? 0),
        conversions_count: Number(row.conversions_count ?? 0),
        earned_amount: Number(row.earned_amount ?? 0),
        pending_amount: Number(row.pending_amount ?? 0),
        total_sales: Number(row.total_sales ?? 0),
        currency: row.currency ?? 'BRL',
      })) as AffiliateCampaignWithMetrics[];
    },
    staleTime: 30_000,
  });
}

/** Converte o payload em parâmetros p_* da RPC `upsert_referral_campaign`. */
function buildUpsertParams(
  payload: ReferralCampaignPayload,
  tenantId: string,
  id: string | null,
) {
  return {
    p_id: id,
    p_program_id: payload.program_id,
    p_name: payload.name,
    p_slug: payload.slug,
    p_description: payload.description,
    p_reward_type: payload.reward_type,
    p_reward_value: payload.reward_value,
    p_starts_at: payload.starts_at,
    p_ends_at: payload.ends_at,
    p_max_per_affiliate: payload.max_per_affiliate,
    p_max_referrals: payload.max_referrals,
    p_destination_url: payload.destination_url,
    p_default_utm_source: payload.default_utm_source,
    p_default_utm_medium: payload.default_utm_medium,
    p_default_utm_campaign: payload.default_utm_campaign,
    p_default_utm_content: payload.default_utm_content,
    p_tiered_rules: payload.tiered_rules,
    p_is_active: payload.is_active,
    p_landing_mode: payload.landing_mode,
    p_landing_title: payload.landing_title,
    p_landing_subtitle: payload.landing_subtitle,
    p_landing_hero_image_url: payload.landing_hero_image_url,
    p_landing_cta_label: payload.landing_cta_label,
    p_landing_thank_you_title: payload.landing_thank_you_title,
    p_landing_thank_you_message: payload.landing_thank_you_message,
    p_landing_redirect_after_submit_seconds:
      payload.landing_redirect_after_submit_seconds,
    p_landing_form_fields: payload.landing_form_fields,
    p_crm_inherit_from_program: false, // v1.1.0 — flag obsoleta, override granular sempre ativo
    p_auto_create_deal: payload.auto_create_deal,
    p_crm_workspace_id: payload.crm_workspace_id,
    p_crm_pipeline_id: payload.crm_pipeline_id,
    p_crm_phase_id: payload.crm_phase_id,
    p_crm_source: payload.crm_source,
    p_crm_default_seller_id: payload.crm_default_seller_id,
    p_crm_distribution_queue_id: payload.crm_distribution_queue_id,
    p_crm_default_tags: payload.crm_default_tags ?? [],
    p_crm_deal_title_template: payload.crm_deal_title_template,
    p_tenant_hint: tenantId,
    // v1.3.0 — modo de atribuição (fixed_seller | queue | null = herda do programa)
    p_crm_assignment_mode: payload.crm_assignment_mode ?? null,
    // v2.0.0 (2026-05-01) — Capa + Regras
    p_cover_media_url: payload.cover_media_url ?? null,
    p_cover_media_type: payload.cover_media_type ?? null,
    p_cover_storage_path: payload.cover_storage_path ?? null,
    p_terms_template_id: payload.terms_template_id ?? null,
    // v2.1.0 — campos do formulário de nova indicação (default true)
    p_referral_form_show_email: payload.referral_form_show_email ?? true,
    p_referral_form_show_message: payload.referral_form_show_message ?? true,
  };
}

/** Mutações de campanhas via RPCs atômicas. */
export function useReferralCampaignMutations() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['referral-campaigns', tenantId] });

  const createMutation = useMutation({
    mutationFn: async (payload: ReferralCampaignPayload): Promise<string> => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const params = buildUpsertParams(payload, tenantId, null);
      const { data, error } = await (supabase.rpc as any)(
        'upsert_referral_campaign',
        params,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (params: { id: string; patch: ReferralCampaignPayload }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const rpcParams = buildUpsertParams(params.patch, tenantId, params.id);
      const { data, error } = await (supabase.rpc as any)(
        'upsert_referral_campaign',
        rpcParams,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await (supabase.rpc as any)('delete_referral_campaign', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { createMutation, updateMutation, deleteMutation };
}
